/**
 * MietwagenRechnungGenerator — Professionelle Mietwagen-Rechnungen
 *
 * Features:
 * - Automatische Rechnungsnummer (MG-YYYY-NNNN)
 * - MietGo / LCS GmbH Briefkopf
 * - Positionen: Mietdauer × Tagessatz, Eigenersparnis, Zustellung, etc.
 * - MwSt-Berechnung (19%)
 * - PDF-Druck mit professionellem Layout
 * - Speicherung in Supabase
 * - Festschreibung: Rechnung finalisieren und in rechnungen-Tabelle sichern
 */
import { useState, useCallback, useEffect } from 'react';
import {
  FileText, Download, Printer, Eye, Edit3, Save, X, Plus, Trash2,
  Calculator, Send, Lock, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { erstelleFestschreibungData, erstelleRechnungsBemerkung } from '../lib/mietwagen';
import { MIETGO_BRIEFKOPF } from '../lib/lcs-briefkopf';

// ============================================================
// TYPES
// ============================================================
interface RechnungsPosition {
  id: string;
  beschreibung: string;
  menge: number;
  einheit: string;
  einzelpreis: number;
  gesamt: number;
}

interface RechnungsDaten {
  rechnungsnr: string;
  rechnungsdatum: string;
  leistungszeitraum_von: string;
  leistungszeitraum_bis: string;
  faellig_am: string;
  positionen: RechnungsPosition[];
  netto: number;
  mwst_satz: number;
  mwst_betrag: number;
  brutto: number;
  zahlungsziel_tage: number;
  bemerkung: string;
}

interface EmpfaengerDaten {
  firma: string;
  name: string;
  strasse: string;
  plz_ort: string;
  schadennr: string;
}

interface AkteDaten {
  id?: string;
  akten_nummer?: string;
  kunde_name?: string;
  kennzeichen?: string;
  fahrzeug_kennzeichen?: string;
  versicherung?: string;
  versicherung_adresse?: string;
  versicherung_plz?: string;
  versicherung_ort?: string;
  versicherung_email?: string;
  schadennummer?: string;
  mietbeginn?: string;
  mietende?: string;
  tagessatz?: number;
  mietdauer_tage?: number;
}

interface MietwagenRechnungGeneratorProps {
  akte?: Partial<AkteDaten> | Record<string, any>;
  onClose?: () => void;
  onSave?: (html: string, rechnungsnr: string) => void;
  onUpdate?: () => void;
}

// ============================================================
// BRIEFKOPF
// ============================================================
// MIETGO_BRIEFKOPF imported from lib/lcs-briefkopf — single source of truth

// ============================================================
// HELPER
// ============================================================
const generateId = () => Math.random().toString(36).substring(2, 9);

const formatCurrency = (val: number) =>
  val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const calcDays = (von: string, bis: string) => {
  if (!von || !bis) return 0;
  const d1 = new Date(von);
  const d2 = new Date(bis);
  return Math.max(1, Math.ceil((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1);
};

// ============================================================
// COMPONENT
// ============================================================
export default function MietwagenRechnungGenerator({
  akte,
  onClose,
  onSave,
  onUpdate,
}: MietwagenRechnungGeneratorProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [festgeschrieben, setFestgeschrieben] = useState(false);
  const [festschreibungLoading, setFestschreibungLoading] = useState(false);
  const [existingRechnungId, setExistingRechnungId] = useState<string | null>(null);

  // Prüfe ob bereits eine festgeschriebene Rechnung existiert
  useEffect(() => {
    if (!akte?.id) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('rechnungen')
          .select('id, rechnungs_nr, status')
          .eq('akte_id', akte.id)
          .eq('business_unit', 'mietwagen')
          .in('status', ['gesendet', 'bezahlt'])
          .limit(1)
          .maybeSingle();
        if (data) {
          setFestgeschrieben(true);
          setExistingRechnungId(data.id);
        }
      } catch {
        // ignore — no festgeschriebene Rechnung
      }
    })();
  }, [akte?.id]);

  // Empfänger (Versicherung)
  const [empfaenger, setEmpfaenger] = useState<EmpfaengerDaten>({
    firma: akte?.versicherung || '',
    name: 'Schadenabteilung',
    strasse: akte?.versicherung_adresse || '',
    plz_ort: [akte?.versicherung_plz, akte?.versicherung_ort].filter(Boolean).join(' '),
    schadennr: akte?.schadennummer || '',
  });

  // Mietdauer berechnen
  const mietTage = akte?.mietdauer_tage || calcDays(akte?.mietbeginn || '', akte?.mietende || '');
  const tagessatz = akte?.tagessatz || 65;

  // Rechnung
  const [rechnung, setRechnung] = useState<RechnungsDaten>(() => {
    const heute = new Date().toISOString().split('T')[0];
    const faellig = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];
    const mietkosten = mietTage * tagessatz;

    return {
      rechnungsnr: `MG-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
      rechnungsdatum: heute,
      leistungszeitraum_von: akte?.mietbeginn || '',
      leistungszeitraum_bis: akte?.mietende || '',
      faellig_am: faellig,
      positionen: [
        {
          id: generateId(),
          beschreibung: `Mietwagenkosten Unfallersatzfahrzeug (${mietTage} Tage × ${formatCurrency(tagessatz)}/Tag)`,
          menge: mietTage,
          einheit: 'Tage',
          einzelpreis: tagessatz,
          gesamt: mietkosten,
        },
      ],
      netto: mietkosten,
      mwst_satz: 19,
      mwst_betrag: mietkosten * 0.19,
      brutto: mietkosten * 1.19,
      zahlungsziel_tage: 14,
      bemerkung: erstelleRechnungsBemerkung(akte || {}),
    };
  });

  // Recalculate totals
  const recalculate = useCallback((positionen: RechnungsPosition[]) => {
    const netto = positionen.reduce((sum, p) => sum + p.gesamt, 0);
    const mwst = netto * (rechnung.mwst_satz / 100);
    setRechnung(prev => ({
      ...prev,
      positionen,
      netto,
      mwst_betrag: mwst,
      brutto: netto + mwst,
    }));
  }, [rechnung.mwst_satz]);

  const addPosition = () => {
    const newPos: RechnungsPosition = {
      id: generateId(),
      beschreibung: '',
      menge: 1,
      einheit: 'Stk.',
      einzelpreis: 0,
      gesamt: 0,
    };
    recalculate([...rechnung.positionen, newPos]);
  };

  const updatePosition = (id: string, field: keyof RechnungsPosition, value: string | number) => {
    const updated = rechnung.positionen.map(p => {
      if (p.id !== id) return p;
      const np = { ...p, [field]: value };
      if (field === 'menge' || field === 'einzelpreis') {
        np.gesamt = np.menge * np.einzelpreis;
      }
      return np;
    });
    recalculate(updated);
  };

  const removePosition = (id: string) => {
    recalculate(rechnung.positionen.filter(p => p.id !== id));
  };

  // Vordefinierte Positionen hinzufügen
  const addStandardPosition = (typ: string) => {
    let pos: RechnungsPosition;
    switch (typ) {
      case 'zustellung':
        pos = { id: generateId(), beschreibung: 'Zustellung des Ersatzfahrzeugs', menge: 1, einheit: 'pauschal', einzelpreis: 35, gesamt: 35 };
        break;
      case 'abholung':
        pos = { id: generateId(), beschreibung: 'Abholung des Ersatzfahrzeugs', menge: 1, einheit: 'pauschal', einzelpreis: 35, gesamt: 35 };
        break;
      case 'winterreifen':
        pos = { id: generateId(), beschreibung: 'Winterreifen-Zuschlag', menge: mietTage, einheit: 'Tage', einzelpreis: 5, gesamt: mietTage * 5 };
        break;
      case 'vollkasko':
        pos = { id: generateId(), beschreibung: 'Haftungsreduzierung / Vollkasko-Schutz', menge: mietTage, einheit: 'Tage', einzelpreis: 12, gesamt: mietTage * 12 };
        break;
      case 'km':
        pos = { id: generateId(), beschreibung: 'Zusätzliche Kilometer', menge: 0, einheit: 'km', einzelpreis: 0.25, gesamt: 0 };
        break;
      case 'eigenersparnis':
        pos = { id: generateId(), beschreibung: 'Abzug Eigenersparnis (3-5%)', menge: 1, einheit: 'pauschal', einzelpreis: -(rechnung.netto * 0.04), gesamt: -(rechnung.netto * 0.04) };
        break;
      default:
        pos = { id: generateId(), beschreibung: '', menge: 1, einheit: 'Stk.', einzelpreis: 0, gesamt: 0 };
    }
    recalculate([...rechnung.positionen, pos]);
  };

  // ============================================================
  // GENERATE PRINT HTML
  // ============================================================
  const generatePrintHTML = useCallback(() => {
    const bk = MIETGO_BRIEFKOPF;
    const emp = empfaenger;
    const r = rechnung;

    const positionenRows = r.positionen.map((p, i) => `
      <tr>
        <td class="pos-nr">${i + 1}</td>
        <td class="pos-beschreibung">${p.beschreibung}</td>
        <td class="pos-menge">${p.menge} ${p.einheit}</td>
        <td class="pos-einzelpreis">${formatCurrency(p.einzelpreis)}</td>
        <td class="pos-gesamt">${formatCurrency(p.gesamt)}</td>
      </tr>
    `).join('');

    const bemerkungHTML = r.bemerkung
      ? `<div class="bemerkung"><strong>Hinweise:</strong><br/>${r.bemerkung.replace(/\n/g, '<br/>')}</div>`
      : '';

    return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>Rechnung ${r.rechnungsnr}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Montserrat:wght@400;500;600;700;800&display=swap');

  @page {
    size: A4;
    margin: 32mm 20mm 28mm 25mm;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 10pt;
    color: #1a1a1a;
    line-height: 1.55;
    background: white;
    padding: 20mm 20mm 28mm 25mm;
  }

  /* ===== HEADER ===== */
  .kopf-container {
    display: flex;
    align-items: center;
    margin-bottom: 2mm;
    height: 16mm;
    gap: 3mm;
  }
  .kopf-logo img { height: 14mm; width: auto; object-fit: contain; }
  .kopf-text {
    flex: 1;
    background: #e8edf2;
    border-radius: 1.5mm;
    padding: 2mm 4mm;
  }
  .kopf-firmenname {
    font-family: 'Montserrat', sans-serif;
    font-size: 11pt;
    font-weight: 700;
    color: #1a1a1a;
  }
  .kopf-inhaber { font-size: 8.5pt; font-weight: 500; color: #333; }
  .kopf-quali { font-size: 7pt; color: #666; }

  /* ===== ABSENDER / EMPFÄNGER ===== */
  .absender-zeile {
    font-size: 6.5pt;
    color: #999;
    border-bottom: 0.3pt solid #ccc;
    padding-bottom: 1mm;
    margin: 6mm 0 3mm 0;
  }
  .adress-bereich {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8mm;
  }
  .empfaenger { font-size: 10pt; line-height: 1.6; }
  .empfaenger .firma { font-weight: 600; }
  .kontakt-block {
    text-align: right;
    font-size: 8pt;
    color: #555;
    line-height: 1.6;
  }
  .kontakt-block strong { color: #333; }

  /* ===== RECHNUNG TITEL ===== */
  .rechnung-titel {
    font-family: 'Montserrat', sans-serif;
    font-size: 14pt;
    font-weight: 700;
    margin-bottom: 2mm;
    color: #1a1a1a;
  }
  .rechnung-meta {
    display: flex;
    gap: 8mm;
    font-size: 9pt;
    color: #555;
    margin-bottom: 6mm;
    padding-bottom: 3mm;
    border-bottom: 0.3pt solid #ddd;
  }
  .rechnung-meta strong { color: #333; }

  /* ===== POSITIONEN TABELLE ===== */
  .positionen-tabelle {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 4mm;
  }
  .positionen-tabelle thead th {
    font-size: 7.5pt;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 2mm 2mm;
    border-bottom: 0.5pt solid #333;
    text-align: left;
  }
  .positionen-tabelle thead th:nth-child(3),
  .positionen-tabelle thead th:nth-child(4),
  .positionen-tabelle thead th:nth-child(5) { text-align: right; }
  .positionen-tabelle tbody td {
    font-size: 9pt;
    padding: 2.5mm 2mm;
    border-bottom: 0.3pt solid #eee;
    vertical-align: top;
  }
  .pos-nr { width: 8mm; color: #999; }
  .pos-beschreibung { }
  .pos-menge { text-align: right; width: 22mm; white-space: nowrap; }
  .pos-einzelpreis { text-align: right; width: 25mm; white-space: nowrap; }
  .pos-gesamt { text-align: right; width: 28mm; white-space: nowrap; font-weight: 500; }

  /* ===== SUMMEN ===== */
  .summen-block {
    display: flex;
    justify-content: flex-end;
    margin-top: 3mm;
    margin-bottom: 6mm;
  }
  .summen-tabelle {
    border-collapse: collapse;
  }
  .summen-tabelle td {
    font-size: 9pt;
    padding: 1.5mm 3mm;
  }
  .summen-tabelle .label { text-align: right; color: #555; }
  .summen-tabelle .wert { text-align: right; font-weight: 500; min-width: 28mm; }
  .summen-tabelle .brutto-zeile td {
    font-size: 11pt;
    font-weight: 700;
    border-top: 0.5pt solid #333;
    padding-top: 2.5mm;
  }

  /* ===== BEMERKUNG ===== */
  .bemerkung {
    font-size: 8.5pt;
    color: #555;
    margin-top: 4mm;
    padding: 3mm;
    background: #f8f9fa;
    border-radius: 1.5mm;
    line-height: 1.6;
  }

  /* ===== ZAHLUNGSHINWEIS ===== */
  .zahlungshinweis {
    margin-top: 6mm;
    font-size: 9pt;
    line-height: 1.6;
  }

  /* ===== FOOTER ===== */
  .fuss-data {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    padding: 2mm 25mm 8mm 25mm;
    border-top: 0.3pt solid #ccc;
    font-size: 6.5pt;
    color: #888;
    line-height: 1.5;
  }
  .fuss-data .fc { flex: 1; }
  .fuss-data .fc:not(:last-child) { margin-right: 3mm; }
  .fuss-data strong { color: #666; font-weight: 600; }

  @media print {
    body { padding: 0; margin: 20mm; }
    .fuss-data { padding: 2mm 0 0 0; position: fixed; bottom: 10mm; left: 25mm; right: 20mm; }
  }
</style>
</head>
<body>

<!-- HEADER -->
<div class="kopf-container">
  <div class="kopf-logo"><img src="${bk.logo_url}" alt="${bk.zusatz}" /></div>
  <div class="kopf-text">
    <div class="kopf-firmenname">${bk.firmenname}</div>
    <div class="kopf-inhaber">${bk.titel}</div>
    <div class="kopf-quali">${bk.qualifikation}</div>
  </div>
</div>

<!-- ABSENDER -->
<div class="absender-zeile">${bk.zusatz} · ${bk.adresse} · ${bk.plz_ort}</div>

<!-- EMPFÄNGER + KONTAKT -->
<div class="adress-bereich">
  <div class="empfaenger">
    <div class="firma">${emp.firma}</div>
    <div>${emp.name}</div>
    ${emp.strasse ? `<div>${emp.strasse}</div>` : ''}
    ${emp.plz_ort ? `<div>${emp.plz_ort}</div>` : ''}
  </div>
  <div class="kontakt-block">
    <strong>Kontakt</strong><br/>
    E-Mail: ${bk.email}<br/>
    Web: ${bk.web}
  </div>
</div>

<!-- RECHNUNG TITEL -->
<div class="rechnung-titel">Rechnung</div>
<div class="rechnung-meta">
  <div><strong>Rechnungsnr.:</strong> ${r.rechnungsnr}</div>
  <div><strong>Datum:</strong> ${formatDate(r.rechnungsdatum)}</div>
  ${r.leistungszeitraum_von ? `<div><strong>Leistungszeitraum:</strong> ${formatDate(r.leistungszeitraum_von)} — ${formatDate(r.leistungszeitraum_bis)}</div>` : ''}
  ${emp.schadennr ? `<div><strong>Schadennr.:</strong> ${emp.schadennr}</div>` : ''}
</div>

<!-- POSITIONEN -->
<table class="positionen-tabelle">
  <thead>
    <tr>
      <th>Pos.</th>
      <th>Beschreibung</th>
      <th>Menge</th>
      <th>Einzelpreis</th>
      <th>Gesamt</th>
    </tr>
  </thead>
  <tbody>
    ${positionenRows}
  </tbody>
</table>

<!-- SUMMEN -->
<div class="summen-block">
  <table class="summen-tabelle">
    <tr>
      <td class="label">Nettobetrag:</td>
      <td class="wert">${formatCurrency(r.netto)}</td>
    </tr>
    <tr>
      <td class="label">MwSt. ${r.mwst_satz}%:</td>
      <td class="wert">${formatCurrency(r.mwst_betrag)}</td>
    </tr>
    <tr class="brutto-zeile">
      <td class="label">Gesamtbetrag:</td>
      <td class="wert">${formatCurrency(r.brutto)}</td>
    </tr>
  </table>
</div>

${bemerkungHTML}

<!-- ZAHLUNGSHINWEIS -->
<div class="zahlungshinweis">
  <p>Bitte überweisen Sie den Gesamtbetrag von <strong>${formatCurrency(r.brutto)}</strong> bis zum <strong>${formatDate(r.faellig_am)}</strong> auf das unten genannte Konto.</p>
  <p style="margin-top: 2mm;">Verwendungszweck: <strong>${r.rechnungsnr}${emp.schadennr ? ' / ' + emp.schadennr : ''}</strong></p>
</div>

<!-- FOOTER -->
<div class="fuss-data">
  <div class="fc">
    ${bk.firmenname}<br/>
    ${bk.titel}<br/>
    ${bk.adresse}<br/>
    ${bk.plz_ort}
  </div>
  <div class="fc">
    <strong>Kontakt</strong><br/>
    Mail: ${bk.email}<br/>
    Web: ${bk.web}
  </div>
  <div class="fc">
    <strong>${bk.bankname}</strong><br/>
    IBAN: ${bk.iban}<br/>
    BIC: ${bk.bic}<br/>
    St.-Nr.: ${bk.steuernummer}${bk.ust_id ? `<br/>USt-ID: ${bk.ust_id}` : ''}
  </div>
</div>

</body>
</html>`;
  }, [empfaenger, rechnung]);

  // Print
  const handlePrint = useCallback(() => {
    const html = generatePrintHTML();
    const w = window.open('', '_blank');
    if (!w) { toast.error('Popup-Blocker aktiv'); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 500);
  }, [generatePrintHTML]);

  // Download
  const handleDownload = useCallback(() => {
    const html = generatePrintHTML();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Rechnung_${rechnung.rechnungsnr}_${empfaenger.firma?.replace(/\s+/g, '_') || 'Rechnung'}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Rechnung heruntergeladen');
  }, [generatePrintHTML, rechnung.rechnungsnr, empfaenger.firma]);

  // Save
  const handleSave = useCallback(async () => {
    const html = generatePrintHTML();
    if (onSave) {
      onSave(html, rechnung.rechnungsnr);
    }

    // Save to DB if akte has id
    if (akte?.id) {
      try {
        await supabase.from('akten').update({
          rechnung_nr: rechnung.rechnungsnr,
          rechnung_datum: rechnung.rechnungsdatum,
          rechnung_betrag: rechnung.brutto,
        }).eq('id', akte.id);
        toast.success('Rechnung gespeichert', { description: rechnung.rechnungsnr });
      } catch {
        toast.error('Fehler beim Speichern');
      }
    } else {
      toast.success('Rechnung erstellt', { description: rechnung.rechnungsnr });
    }
  }, [generatePrintHTML, rechnung, akte, onSave]);

  // Festschreiben — Rechnung finalisieren und in rechnungen-Tabelle sichern
  const handleFestschreiben = useCallback(async () => {
    if (!akte?.id) {
      toast.error('Akte muss gespeichert sein');
      return;
    }
    if (festgeschrieben) {
      toast.info('Rechnung ist bereits festgeschrieben');
      return;
    }
    if (!rechnung.rechnungsnr || rechnung.brutto <= 0) {
      toast.error('Bitte Rechnungsnummer und mindestens eine Position eintragen');
      return;
    }

    setFestschreibungLoading(true);
    try {
      // 1. Festschreibungs-Daten erstellen
      const festData = erstelleFestschreibungData(
        rechnung.rechnungsnr,
        akte.id as string,
        empfaenger,
        rechnung,
        {
          kennzeichen: akte.kennzeichen || akte.fahrzeug_kennzeichen,
          versicherung: akte.versicherung,
          schadennummer: akte.schadennummer,
        },
      );

      // 2. Rechnung in DB erstellen
      const { data: rechnungRow, error: rechnungError } = await supabase
        .from('rechnungen')
        .insert(festData)
        .select('id')
        .single();
      if (rechnungError) throw rechnungError;

      // 3. Positionen in rechnungspositionen speichern
      if (rechnungRow?.id) {
        const posInserts = rechnung.positionen.map((p, idx) => ({
          rechnung_id: rechnungRow.id,
          position_nr: idx + 1,
          bezeichnung: p.beschreibung,
          menge: p.menge,
          einheit: p.einheit,
          einzelpreis: p.einzelpreis,
          mwst_satz: rechnung.mwst_satz,
          netto_betrag: p.gesamt,
        }));
        await supabase.from('rechnungspositionen').insert(posInserts);
      }

      // 4. Akte aktualisieren
      await supabase.from('akten').update({
        rechnung_nr: rechnung.rechnungsnr,
        rechnung_datum: rechnung.rechnungsdatum,
        rechnung_betrag: rechnung.brutto,
        rechnung_festgeschrieben: true,
      }).eq('id', akte.id);

      setFestgeschrieben(true);
      setExistingRechnungId(rechnungRow?.id || null);
      toast.success('Rechnung festgeschrieben', {
        description: `${rechnung.rechnungsnr} — ${formatCurrency(rechnung.brutto)}`,
      });

      // 5. Komplett-Dokument automatisch erzeugen (fire-and-forget, kein Fehler bei Misserfolg)
      // fetch MUSS vor onUpdate starten — onUpdate löst window.location.reload() aus,
      // was den laufenden Fetch abbrechen würde bevor das PDF gespeichert ist.
      fetch('/api/rechnungen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ akteId: akte.id }),
      }).catch(() => {/* Komplett-PDF Erzeugung nicht kritisch */});

      onUpdate?.();
    } catch (err: any) {
      toast.error('Fehler beim Festschreiben', { description: err.message });
    } finally {
      setFestschreibungLoading(false);
    }
  }, [akte, rechnung, empfaenger, festgeschrieben]);

  // ============================================================
  // RENDER — EDIT MODE
  // ============================================================
  if (mode === 'edit') {
    return (
      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        {/* Festgeschrieben Banner */}
        {festgeschrieben && (
          <div className="flex items-center gap-2 px-5 py-2.5 bg-green-50 border-b border-green-200">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-800">
              Rechnung festgeschrieben — Änderungen nicht mehr möglich
            </span>
            <Lock className="w-3.5 h-3.5 text-green-500 ml-auto" />
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-orange-50 to-white">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-600" />
            <h2 className="text-base font-semibold">Mietwagen-Rechnung erstellen</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setMode('preview')} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition">
              <Eye className="w-3.5 h-3.5" /> Vorschau
            </button>
            <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white hover:bg-green-700 rounded-lg transition">
              <Save className="w-3.5 h-3.5" /> Speichern
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <fieldset disabled={festgeschrieben} className="p-5 space-y-5">
          {/* Rechnungsdaten */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Rechnungsnr.</label>
              <input
                type="text"
                value={rechnung.rechnungsnr}
                onChange={e => setRechnung(prev => ({ ...prev, rechnungsnr: e.target.value }))}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Rechnungsdatum</label>
              <input
                type="date"
                value={rechnung.rechnungsdatum}
                onChange={e => setRechnung(prev => ({ ...prev, rechnungsdatum: e.target.value }))}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Leistung von</label>
              <input
                type="date"
                value={rechnung.leistungszeitraum_von}
                onChange={e => setRechnung(prev => ({ ...prev, leistungszeitraum_von: e.target.value }))}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Leistung bis</label>
              <input
                type="date"
                value={rechnung.leistungszeitraum_bis}
                onChange={e => setRechnung(prev => ({ ...prev, leistungszeitraum_bis: e.target.value }))}
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
              />
            </div>
          </div>

          {/* Empfänger */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 mb-2">Empfänger (Versicherung)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Firma</label>
                <input
                  type="text"
                  value={empfaenger.firma}
                  onChange={e => setEmpfaenger(prev => ({ ...prev, firma: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Abteilung / z.Hd.</label>
                <input
                  type="text"
                  value={empfaenger.name}
                  onChange={e => setEmpfaenger(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Straße</label>
                <input
                  type="text"
                  value={empfaenger.strasse}
                  onChange={e => setEmpfaenger(prev => ({ ...prev, strasse: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">PLZ Ort</label>
                <input
                  type="text"
                  value={empfaenger.plz_ort}
                  onChange={e => setEmpfaenger(prev => ({ ...prev, plz_ort: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Schadennummer</label>
                <input
                  type="text"
                  value={empfaenger.schadennr}
                  onChange={e => setEmpfaenger(prev => ({ ...prev, schadennr: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                />
              </div>
            </div>
          </div>

          {/* Positionen */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-gray-700">Positionen</h3>
              <div className="flex items-center gap-1">
                <button onClick={() => addStandardPosition('zustellung')} className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded transition">+ Zustellung</button>
                <button onClick={() => addStandardPosition('abholung')} className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded transition">+ Abholung</button>
                <button onClick={() => addStandardPosition('winterreifen')} className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded transition">+ Winterreifen</button>
                <button onClick={() => addStandardPosition('vollkasko')} className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 rounded transition">+ Vollkasko</button>
                <button onClick={() => addStandardPosition('eigenersparnis')} className="px-2 py-1 text-[10px] bg-orange-100 hover:bg-orange-200 text-orange-700 rounded transition">- Eigenersparnis</button>
                <button onClick={addPosition} className="flex items-center gap-1 px-2 py-1 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition">
                  <Plus className="w-3 h-3" /> Position
                </button>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left w-8">#</th>
                    <th className="px-3 py-2 text-left">Beschreibung</th>
                    <th className="px-3 py-2 text-right w-20">Menge</th>
                    <th className="px-3 py-2 text-left w-16">Einheit</th>
                    <th className="px-3 py-2 text-right w-24">Einzelpreis</th>
                    <th className="px-3 py-2 text-right w-24">Gesamt</th>
                    <th className="px-3 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rechnung.positionen.map((pos, i) => (
                    <tr key={pos.id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-3 py-1">
                        <input
                          type="text"
                          value={pos.beschreibung}
                          onChange={e => updatePosition(pos.id, 'beschreibung', e.target.value)}
                          className="w-full px-2 py-1 text-sm border-0 bg-transparent focus:ring-1 focus:ring-orange-200 rounded"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          type="number"
                          value={pos.menge}
                          onChange={e => updatePosition(pos.id, 'menge', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-sm text-right border-0 bg-transparent focus:ring-1 focus:ring-orange-200 rounded"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          type="text"
                          value={pos.einheit}
                          onChange={e => updatePosition(pos.id, 'einheit', e.target.value)}
                          className="w-full px-2 py-1 text-sm border-0 bg-transparent focus:ring-1 focus:ring-orange-200 rounded"
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input
                          type="number"
                          step="0.01"
                          value={pos.einzelpreis}
                          onChange={e => updatePosition(pos.id, 'einzelpreis', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-sm text-right border-0 bg-transparent focus:ring-1 focus:ring-orange-200 rounded"
                        />
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(pos.gesamt)}</td>
                      <td className="px-2 py-2">
                        <button onClick={() => removePosition(pos.id)} className="p-1 text-gray-300 hover:text-red-500 transition">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Summen */}
            <div className="flex justify-end mt-3">
              <div className="w-64 space-y-1">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Netto:</span>
                  <span>{formatCurrency(rechnung.netto)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>MwSt. {rechnung.mwst_satz}%:</span>
                  <span>{formatCurrency(rechnung.mwst_betrag)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-1 border-t">
                  <span>Brutto:</span>
                  <span>{formatCurrency(rechnung.brutto)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bemerkung */}
          <div>
            <label className="block text-[10px] font-medium text-gray-500 uppercase mb-1">Bemerkung / Hinweise</label>
            <textarea
              value={rechnung.bemerkung}
              onChange={e => setRechnung(prev => ({ ...prev, bemerkung: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t flex-wrap">
            <button onClick={handlePrint} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition">
              <Printer className="w-4 h-4" /> Drucken
            </button>
            <button onClick={handleDownload} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition">
              <Download className="w-4 h-4" /> Download HTML
            </button>
            <div className="flex items-center gap-2 ml-auto">
              {!festgeschrieben && (
                <button onClick={handleSave} className="flex items-center gap-1.5 px-4 py-2 text-sm bg-orange-600 text-white hover:bg-orange-700 rounded-lg transition">
                  <Save className="w-4 h-4" /> Speichern
                </button>
              )}
              {festgeschrieben ? (
                <span className="flex items-center gap-1.5 px-4 py-2 text-sm text-green-700 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle2 className="w-4 h-4" /> Festgeschrieben
                </span>
              ) : (
                <button
                  onClick={handleFestschreiben}
                  disabled={festschreibungLoading}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white hover:bg-green-700 rounded-lg transition disabled:opacity-50"
                >
                  {festschreibungLoading ? <Calculator className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  Festschreiben
                </button>
              )}
            </div>
          </div>
        </fieldset>
      </div>
    );
  }

  // ============================================================
  // RENDER — PREVIEW MODE
  // ============================================================
  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-orange-50 to-white">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-orange-600" />
          <h2 className="text-base font-semibold">Vorschau — {rechnung.rechnungsnr}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMode('edit')} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            <Edit3 className="w-3.5 h-3.5" /> Bearbeiten
          </button>
          <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            <Printer className="w-3.5 h-3.5" /> Drucken
          </button>
          <button onClick={handleDownload} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-green-600 text-white hover:bg-green-700 rounded-lg transition">
            <Save className="w-3.5 h-3.5" /> Speichern
          </button>
          {onClose && (
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="p-5">
        <div
          className="mx-auto bg-white shadow-lg border"
          style={{ width: '210mm', minHeight: '297mm', padding: '20mm', transform: 'scale(0.7)', transformOrigin: 'top center' }}
        >
          <iframe
            srcDoc={generatePrintHTML()}
            className="w-full border-0"
            style={{ height: '297mm', width: '210mm' }}
            title="Rechnungsvorschau"
          />
        </div>
      </div>
    </div>
  );
}
